import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  TrendingUp, Users, MousePointer, DollarSign, 
  Eye, BarChart3, Target, Calendar 
} from 'lucide-react';
import axios from 'axios';

const AdAnalyticsDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(7);

  useEffect(() => {
    setupDemoAds();
  }, []);

  const setupDemoAds = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ads/demo/setup`);
      console.log('Demo ads setup completed');
    } catch (error) {
      console.error('Error setting up demo ads:', error);
    }
  };

  const fetchAnalytics = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads/analytics/${campaignId}?days=${timeRange}`
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Demo data for display
  const demoCampaigns = [
    {
      id: "1",
      name: "Premium AI Assistant Services",
      status: "active",
      budget: 1000,
      spent: 234.50,
      impressions: 15420,
      clicks: 187,
      ctr: 1.21
    },
    {
      id: "2", 
      name: "Fashion & Style for Dating",
      status: "active",
      budget: 800,
      spent: 156.75,
      impressions: 9830,
      clicks: 94,
      ctr: 0.96
    },
    {
      id: "3",
      name: "Local Date Experiences", 
      status: "active",
      budget: 600,
      spent: 89.20,
      impressions: 6740,
      clicks: 78,
      ctr: 1.16
    }
  ];

  const totalStats = {
    totalImpressions: demoCampaigns.reduce((sum, c) => sum + c.impressions, 0),
    totalClicks: demoCampaigns.reduce((sum, c) => sum + c.clicks, 0),
    totalSpent: demoCampaigns.reduce((sum, c) => sum + c.spent, 0),
    avgCTR: (demoCampaigns.reduce((sum, c) => sum + c.ctr, 0) / demoCampaigns.length).toFixed(2)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
            <BarChart3 className="mr-3 text-rose-400" size={32} />
            Advertising Analytics Dashboard
          </h1>
          <p className="text-gray-300">Monitor ad performance and revenue for Shot Clock Dating</p>
        </div>

        {/* Overview Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Impressions</p>
                  <p className="text-2xl font-bold text-white">{totalStats.totalImpressions.toLocaleString()}</p>
                </div>
                <Eye className="text-blue-400" size={24} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Clicks</p>
                  <p className="text-2xl font-bold text-white">{totalStats.totalClicks.toLocaleString()}</p>
                </div>
                <MousePointer className="text-green-400" size={24} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Ad Revenue</p>
                  <p className="text-2xl font-bold text-white">${totalStats.totalSpent.toFixed(2)}</p>
                </div>
                <DollarSign className="text-rose-400" size={24} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Average CTR</p>
                  <p className="text-2xl font-bold text-white">{totalStats.avgCTR}%</p>
                </div>
                <Target className="text-purple-400" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Performance */}
        <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center">
                <TrendingUp className="mr-2 text-rose-400" size={24} />
                Campaign Performance
              </CardTitle>
              <div className="flex items-center space-x-2">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-1 text-sm"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {demoCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-semibold text-white">{campaign.name}</h3>
                      <Badge 
                        className={`${
                          campaign.status === 'active' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        } border`}
                      >
                        {campaign.status.toUpperCase()}
                      </Badge>
                    </div>
                    <Button
                      onClick={() => fetchAnalytics(campaign.id)}
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      View Details
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-6 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Budget</p>
                      <p className="text-white font-medium">${campaign.budget}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Spent</p>
                      <p className="text-white font-medium">${campaign.spent}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Impressions</p>
                      <p className="text-white font-medium">{campaign.impressions.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Clicks</p>
                      <p className="text-white font-medium">{campaign.clicks}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">CTR</p>
                      <p className="text-white font-medium">{campaign.ctr}%</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Budget Used</p>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                        <div
                          className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(campaign.spent / campaign.budget) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Revenue Insights */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <DollarSign className="mr-2 text-rose-400" size={20} />
                Revenue Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Subscription Revenue</span>
                  <span className="text-white font-semibold">$2,847.50</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Advertising Revenue</span>
                  <span className="text-white font-semibold">${totalStats.totalSpent.toFixed(2)}</span>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">Total Revenue</span>
                    <span className="text-rose-400 font-bold text-lg">
                      ${(2847.50 + totalStats.totalSpent).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Users className="mr-2 text-rose-400" size={20} />
                User Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Free Users Seeing Ads</span>
                  <span className="text-white font-semibold">1,247</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Premium Users (Ad-Free)</span>
                  <span className="text-white font-semibold">189</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Ad Engagement Rate</span>
                  <span className="text-white font-semibold">1.11%</span>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">Revenue per Free User</span>
                    <span className="text-rose-400 font-bold">
                      ${(totalStats.totalSpent / 1247).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdAnalyticsDashboard;